import { adminFetch } from "./_client";
import type { FlowBuilderDefinition, FlowBuilderRow, FlowVersionRow, FlowMissingRichTemplateRef, FlowBuilderTemplateMetric, RichTemplateMetric, FlowSimulatorPreset, FlowAbTestRow } from "./types";


export function fetchFlowBuilderFlows(token: string) {
  return adminFetch<{ flows: FlowBuilderRow[] }>("/admin/flow-builder/flows", token);
}

export function fetchFlowBuilderFlow(token: string, id: string) {
  return adminFetch<{ flow: FlowBuilderRow & { description: string | null }; versions: FlowVersionRow[]; liveDefinition: FlowBuilderDefinition | null; liveVersion: number | null; missingRichTemplates: FlowMissingRichTemplateRef[] }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}`, token,
  );
}

export function fetchFlowBuilderVersion(token: string, id: string, version: number) {
  return adminFetch<{ version: FlowVersionRow & { definition: FlowBuilderDefinition } }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/versions/${version}`, token,
  );
}

export function saveFlowBuilderDraft(token: string, id: string, definition: FlowBuilderDefinition, notes?: string) {
  return adminFetch<{ ok: boolean; version: number; id: number }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/draft`, token,
    { method: "POST", body: JSON.stringify({ definition, notes }) },
  );
}

export function publishFlowBuilderVersion(token: string, id: string, version: number, opts?: { force?: boolean }) {
  const qs = opts?.force ? "?force=true" : "";
  return adminFetch<{ ok: boolean }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/versions/${version}/publish${qs}`, token,
    { method: "POST" },
  );
}

export function setFlowBuilderKillSwitch(token: string, id: string, enabled: boolean) {
  return adminFetch<{ ok: boolean; killSwitched: boolean }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/kill-switch`, token,
    { method: "POST", body: JSON.stringify({ enabled }) },
  );
}

export function seedFlowBuilderFromRegistry(token: string) {
  return adminFetch<{ ok: boolean; inserted: number; skipped: number }>(
    `/admin/flow-builder/flows/_/seed`, token, { method: "POST" },
  );
}

export function simulateFlowBuilder(token: string, id: string, body: { stepId: string; userId: string; context?: { contextKey?: string; data?: Record<string, unknown>; locale?: string } }) {
  return adminFetch<{ ok: boolean; flow: unknown; decisions: Array<{ stepId: string; channel: string; decision: string; reason: string; templateId?: string }> }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/simulate`, token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function fetchFlowBuilderRuns(token: string, id: string, limit = 50) {
  return adminFetch<{ runs: Array<{ id: number; step_id: string; channel: string; template_id: string | null; rich_template_id: string | null; status: string; reason: string | null; ab_variant: string | null; created_at: string; user_id: string | null }> }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/runs?limit=${limit}`, token,
  );
}

export function fetchFlowBuilderAnalytics(token: string, id: string) {
  return adminFetch<{ windowDays: number; total: number; byStatus: Record<string, number>; byChannel: Record<string, number>; byTemplate: FlowBuilderTemplateMetric[]; missingRichTemplateFailures: number; missingRichTemplateIds: Record<string, number> }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/analytics`, token,
  );
}

export function fetchRichTemplateMetrics(token: string, days = 30) {
  return adminFetch<{ windowDays: number; metrics: Record<string, RichTemplateMetric> }>(
    `/admin/flow-builder/rich-template-metrics?days=${days}`, token,
  );
}

export function fetchFlowBuilderSimulatorPresets(token: string, id: string) {
  return adminFetch<{ trigger: string; presets: FlowSimulatorPreset[] }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/simulator-presets`, token,
  );
}

export function fetchFlowAbTests(token: string, id: string) {
  return adminFetch<{ tests: FlowAbTestRow[] }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/ab-tests`, token,
  );
}

export function createFlowAbTest(token: string, id: string, body: { stepId: string; variantA: Record<string, unknown>; variantB: Record<string, unknown>; splitA?: number; conversionMetric?: string; notes?: string }) {
  return adminFetch<{ ok: boolean; id: number }>(
    `/admin/flow-builder/flows/${encodeURIComponent(id)}/ab-tests`, token,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function updateFlowAbTest(token: string, abId: number, body: { splitA?: number; conversionMetric?: string; notes?: string }) {
  return adminFetch<{ ok: boolean }>(`/admin/flow-builder/ab-tests/${abId}`, token, { method: "PATCH", body: JSON.stringify(body) });
}

export function stopFlowAbTest(token: string, abId: number) {
  return adminFetch<{ ok: boolean }>(
    `/admin/flow-builder/ab-tests/${abId}/stop`, token, { method: "POST" },
  );
}

export function promoteFlowAbTest(token: string, abId: number, variant: "a" | "b") {
  return adminFetch<{ ok: boolean; promoted: "a" | "b" }>(
    `/admin/flow-builder/ab-tests/${abId}/promote`, token,
    { method: "POST", body: JSON.stringify({ variant }) },
  );
}

export function runAbPromotionAnalyzer(token: string, dryRun = true) {
  return adminFetch<{ ok: boolean; evaluated: number; promoted: number; decisions: Array<{ abTestId: number; flowId: string; stepId: string; winner: "a" | "b" | null; decision: string; reason: string; rateA: number; rateB: number; exposuresA: number; exposuresB: number }> }>(
    `/admin/flow-builder/ab-tests/_run-promotion`, token,
    { method: "POST", body: JSON.stringify({ dryRun }) },
  );
}
